import React, { useRef, useEffect } from 'react';
import { cn } from '@tasama/ui';
import { NotificationFloatingView } from './NotificationFloatingView';
import type { BackendNotificationData } from '../features/Notifications/types/notification-types';

export interface NotificationFloatingMenuPortalProps {
  /** Array of notifications to display */
  notifications: BackendNotificationData[];
  /** Reference element for positioning (e.g., trigger button) */
  anchorRef: React.RefObject<HTMLElement | null>;
  /** Callback when menu should close */
  onClose: () => void;
  /** Handler for notification click */
  onNotificationClick?: (_notification: BackendNotificationData) => void;
  /** Handler for "View All" click */
  onViewAllClick?: () => void;
  /** Test ID for QA automation */
  testID?: string;
  /** Additional CSS classes */
  className?: string;
  /** Loading state while notifications are being fetched */
  isLoading?: boolean;
}

/**
 * NotificationFloatingMenuPortal Component
 *
 * A portal-based wrapper for NotificationFloatingView that handles:
 * - Positioning relative to anchor element
 * - Click outside to close
 * - Escape key to close
 * - Viewport boundary detection
 */
export const NotificationFloatingMenuPortal: React.FC<
  NotificationFloatingMenuPortalProps
> = ({
  notifications,
  anchorRef,
  onClose,
  onNotificationClick,
  onViewAllClick,
  testID = 'notification-floating-menu-portal',
  className,
  isLoading = false,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  const focusFirstItem = () => {
    requestAnimationFrame(() => {
      const firstItem = menuRef.current?.querySelector<HTMLElement>(
        '[role="button"][tabindex="0"]'
      );
      (firstItem ?? menuRef.current)?.focus();
    });
  };

  // Focus container immediately; items not ready yet if still loading
  useEffect(() => {
    menuRef.current?.focus();
  }, []);

  // Re-focus first item once loading completes
  useEffect(() => {
    if (!isLoading) focusFirstItem();
  }, [isLoading]);

  // Handle Tab to trap focus: close and return to anchor when exiting
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return;

    const focusable = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ) ?? []
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && (document.activeElement === first || !focusable.length)) {
      e.preventDefault();
      onClose();
      anchorRef.current?.focus();
    } else if (
      !e.shiftKey &&
      (document.activeElement === last || !focusable.length)
    ) {
      e.preventDefault();
      onClose();
      anchorRef.current?.focus();
    }
  };

  // Handle click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        anchorRef.current &&
        !anchorRef.current.contains(target)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose, anchorRef]);

  // Handle escape key to close menu
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Position the menu relative to the anchor element
  useEffect(() => {
    if (!anchorRef.current || !menuRef.current) return;

    let rafId: number;

    const syncPosition = () => {
      const menu = menuRef.current;
      const anchor = anchorRef.current;
      if (!menu || !anchor) {
        rafId = requestAnimationFrame(syncPosition);
        return;
      }

      const anchorRect = anchor.getBoundingClientRect();
      const menuRect = menu.getBoundingClientRect();

      // Position below the anchor, aligned to the right edge
      let top = anchorRect.bottom + 8;
      let left = anchorRect.right - menuRect.width;

      // Ensure menu stays within viewport
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      if (left + menuRect.width > viewportWidth) {
        left = viewportWidth - menuRect.width - 8;
      }
      if (left < 8) {
        left = 8;
      }
      if (top + menuRect.height > viewportHeight) {
        top = viewportHeight - menuRect.height - 8;
      }
      if (top < 8) {
        top = 8;
      }

      menu.style.top = `${top}px`;
      menu.style.left = `${left}px`;

      rafId = requestAnimationFrame(syncPosition);
    };

    rafId = requestAnimationFrame(syncPosition);
    return () => cancelAnimationFrame(rafId);
  }, [anchorRef]);

  return (
    <div
      ref={menuRef}
      className={cn(
        'fixed z-[9999]',
        'bg-background',
        'border border-border',
        'rounded-xl',
        'shadow-lg',
        'w-[420px]',
        'max-h-[600px]',
        className
      )}
      data-testid={testID}
      role="menu"
      aria-orientation="vertical"
      tabIndex={-1}
      onKeyDown={handleKeyDown}
    >
      <NotificationFloatingView
        notifications={notifications}
        maxItems={5}
        onNotificationClick={onNotificationClick}
        onViewAllClick={onViewAllClick}
        testID={`${testID}-view`}
        isLoading={isLoading}
      />
    </div>
  );
};

NotificationFloatingMenuPortal.displayName = 'NotificationFloatingMenuPortal';
